import { useEffect, useRef, useState } from 'react'
import { Mascot } from '../components/Mascot'
import type { ReactNode } from 'react'
import { api, type Summary as SummaryData } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { Empty, ErrorScreen, Loading } from '../components/States'
import { Shell } from '../components/Shell'
import { Trace, TraceRegion } from '../lib/trace'
import { Explain } from '../components/Explain'
import { say, sayInFirstLanguage } from '../lib/speech'
import { sfx } from '../lib/sound'
import { outbox } from '../lib/outbox'
import { sessionStore } from '../lib/sessionStore'

/**
 * The end of a session, including Mistake Lab.
 *
 * It says "Finished for today" and leaving is the default: Done is the big
 * button. One quiet "Practise 5 more" sits under it after an ordinary
 * practice, and not after those five (Sam's build list, item 8, 29 Oct 2026;
 * research/ux-deep-dive/02-practice-ending.md: a clear end, one extension,
 * never a chain of them).
 */
export function Summary({
  sessionId, onHome, onBadges, onGarden, onMore,
}: {
  sessionId: string
  onHome: () => void
  onBadges: () => void
  onGarden: () => void
  /** Start the five more. */
  onMore?: () => void
}) {
  const [data, setData] = useState<SummaryData | null>(null)
  const [error, setError] = useState<unknown>(null)
  // Answers still on their way to the server (lib/outbox.ts). The summary is
  // added up from what the server has, so it waits until they are all there.
  const [waiting, setWaiting] = useState(() => outbox.count(sessionId))
  const asked = useRef(false)

  useEffect(() => outbox.subscribe(() => setWaiting(outbox.count(sessionId))), [sessionId])

  const load = () => {
    setError(null)
    asked.current = true
    api.finish(sessionId)
      .then((summary) => {
        setData(summary)
        sessionStore.forget(sessionId)
        // "You are done." A finish line, not a reward: it plays once for
        // every finished session, however it went, and never louder for a
        // better score. See lib/sound.ts.
        sfx.finished()
      })
      .catch(setError)
  }
  useEffect(() => {
    if (waiting > 0) void outbox.flush()
    else if (!asked.current) load()
  }, [waiting, sessionId])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data && waiting > 0) return <Saving waiting={waiting} onHome={onHome} />
  if (!data) return <Loading what="Adding it up" />

  return (
    <Page width={560}>
      {/* The button says Done here, not Home. Same control, right label. */}
      <Shell exitMode="done" onExit={onHome}>
        {/* What the child is told is about the work, not about them (D-049).
            There is no percentage here, and there is no score. Every number
            below is either a fact about today, which nothing can take away,
            or a total that only ever rises (D-048). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Mascot size={72} mood="cheer" label="" />
          <div style={{ flex: 1 }}>
            <Heading sub={<WorkDone data={data} />}>Finished for today</Heading>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <Stat label="Words today" value={String(data.words_today)}
                trace="calc.distinct words among this session's T14 trials"
                explain={
                  <>
                    How many different words you worked on today. A word counts
                    once, however many questions you got about it.
                  </>
                } />
          <Stat label="New today" value={String(data.words_new_today)}
                trace="calc.words whose first ever T14 trial was in this session"
                explain={
                  <>
                    Words you had <b>never seen before today</b>. Meeting a new
                    word is the start — you will see it again over the next few
                    weeks.
                  </>
                } />
          <Stat label="Words grown" value={String(data.words_grown_total)}
                trace="calc.rows in T7 learner_word with grown_at set"
                rising
                explain={
                  <>
                    A word is <b>grown</b> once you have got it right in all three
                    kinds of question — knowing it when you see it, spelling it,
                    and using it — in <b>at least three different practices</b>.
                    <br /><br />
                    It takes weeks on purpose. Remembering something a long time
                    later is what learning it means.
                    <br /><br />
                    <b>This number can only go up.</b> Once a word is grown it
                    stays grown, even if you get it wrong another day.
                  </>
                } />
        </div>

        {/* Only badges this session actually earned, so "New badge!" means
            something. Most sessions will show nothing here, on purpose. */}
        {data.new_badges?.length ? (
          <div style={{ marginBottom: 22 }}>
            {data.new_badges.map((b) => (
              <TraceRegion key={b.code} id="T19"
                           note="joined to T18 badge for the name and picture">
              <Card style={{
                background: theme.greenPale, borderColor: theme.green,
                display: 'flex', gap: 14, alignItems: 'center', marginBottom: 10,
              }}>
                <div style={{ fontSize: 32 }}>
                  <Trace id="T18.emoji">{b.emoji}</Trace>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, color: theme.greenDark }}>
                    NEW BADGE
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: theme.greenDarker }}>
                    <Trace id="T18.name">{b.name}</Trace>
                  </div>
                  <div style={{ fontSize: 15, color: theme.greenDark, marginTop: 2 }}>
                    <Trace id="T18.description">{b.description}</Trace>
                  </div>
                </div>
              </Card>
              </TraceRegion>
            ))}
          </div>
        ) : null}

        <h2 style={{ fontSize: 19, fontWeight: 800, color: theme.text, margin: '0 0 12px' }}>
          Mistake Lab
        </h2>

        {data.mistakes.length === 0 ? (
          <Empty emoji="🎯" title="Nothing to fix" line="That is a good day." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.mistakes.map((m, i) => (
              <TraceRegion key={i} id="T14"
                           note="wrong first attempts in this session">
              <Card style={{ padding: 18 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: theme.text }}>
                  <Trace id="T3.headword">{m.word ?? '—'}</Trace>
                </div>
                {m.your_answer ? (
                  <div style={{ fontSize: 15, color: theme.muted, marginTop: 4 }}>
                    You wrote:{' '}
                    <Trace id="T14.response">
                      <span style={{ color: theme.red }}>{m.your_answer}</span>
                    </Trace>
                  </div>
                ) : null}
                {m.definition ? (
                  <p style={{ margin: '10px 0 0', fontSize: 16, color: theme.text2, lineHeight: 1.5 }}>
                    <Trace id="T4.definition">{m.definition}</Trace>
                  </p>
                ) : null}
                {m.example ? (
                  <p style={{
                    margin: '8px 0 0', fontSize: 15, color: theme.muted,
                    lineHeight: 1.55, fontStyle: 'italic',
                  }}>
                    <Trace id="T4.example_sentence">{m.example}</Trace>
                  </p>
                ) : null}

                {/* Hear it again, in both languages.
                    A child who did not recognise "reluctant" in English may
                    know it perfectly well as 勉強的, and hearing the two
                    together is what joins them up. This is the one screen
                    where that matters most: these are the words they just
                    got wrong. */}
                {m.word ? (
                  <div style={{
                    display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap',
                  }}>
                    <SpeakChip label={`🔊 ${m.word}`}
                               onClick={() => say(m.word as string)} />
                    {m.translation && data.first_language !== 'en' ? (
                      <SpeakChip
                        label={`🔊 ${m.translation}`}
                        onClick={() => sayInFirstLanguage(
                          m.translation as string, data.first_language)}
                      />
                    ) : null}
                  </div>
                ) : null}
              </Card>
              </TraceRegion>
            ))}
            <p style={{ fontSize: 14, color: theme.faint, margin: '2px 0 0', lineHeight: 1.5 }}>
              These come back sooner than the rest. Getting one wrong is how the
              app learns what to show you next.
            </p>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          <Button full tone="child" onClick={onHome}>Done</Button>
        </div>
        {/* Quiet, under Done, and centred: bottom right is where the sound
            buttons float, and they covered it there. */}
        {data.can_practise_more && onMore ? (
          <div style={{ textAlign: 'center', marginTop: 6 }}>
            <button type="button" onClick={onMore} style={{
              fontFamily: 'inherit', fontSize: 15, fontWeight: 700,
              color: theme.muted, background: 'transparent', border: 'none',
              textDecoration: 'underline', cursor: 'pointer', minHeight: TAP_MIN, padding: '0 10px',
            }}>
              Practise 5 more
            </button>
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, marginBottom: 72, flexWrap: 'wrap' }}>
          <Button tone="quiet" onClick={onBadges}>Badges</Button>
          <Button tone="quiet" onClick={onGarden}>My garden</Button>
        </div>
      </Shell>
    </Page>
  )
}

/**
 * The one sentence at the top, in the words D-049 asks for.
 *
 * "You worked through eleven words today, four for the first time" rather
 * than "level 7" or "93%". Aiming at getting better produces more persistence
 * than aiming at looking good, and a number that can fall does neither.
 */
function WorkDone({ data }: { data: SummaryData }) {
  if (!data.words_today) return <>That is today finished.</>
  const words = `${data.words_today} ${data.words_today === 1 ? 'word' : 'words'}`
  const fresh = data.words_new_today
    ? `, ${data.words_new_today} for the first time`
    : ''
  return <>You worked through {words} today{fresh}.</>
}

/** A tap-to-hear button, sized for a small finger. */
function SpeakChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
        color: theme.text2, background: theme.card,
        border: `1.5px solid ${theme.border}`,
        borderRadius: radius.sm, padding: '9px 13px',
        minHeight: TAP_MIN, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}


function Stat({ label, value, trace, rising = false, explain }: {
  label: string; value: string
  /** Where the number came from, for the provenance overlay. */
  trace: string
  /** A total that can only go up. Marked so it reads as an accumulation
   *  rather than as a score (D-048). */
  rising?: boolean
  /**
   * What this number means, in words a child can read.
   *
   * "308 Words grown, all time" is meaningless to a nine-year-old without it,
   * and a number a child cannot interpret is either ignored or misread — and
   * "words grown" is the one number this app most wants understood, because
   * it is the one that measures the slow thing.
   */
  explain?: ReactNode
}) {
  return (
    <div style={{
      flex: '1 1 140px', background: theme.card, borderRadius: radius.md,
      border: `1px solid ${theme.borderWarm}`, padding: '14px 16px',
    }}>
      <div style={{
        fontSize: 26, fontWeight: 800,
        color: rising ? theme.greenDarker : theme.text,
      }}>
        <Trace id={trace}>{rising ? `🌿 ${value}` : value}</Trace>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 13, color: theme.faint, marginTop: 2,
      }}>
        <span>{label}{rising ? ' · all time' : ''}</span>
        {explain ? (
          <Explain label={`what ${label} means`}>{explain}</Explain>
        ) : null}
      </div>
    </div>
  )
}

/** Answers still to send before the session can be added up. */
function Saving({ waiting, onHome }: { waiting: number; onHome: () => void }) {
  const offline = typeof navigator !== 'undefined' && !navigator.onLine
  // Only promise "safe" when the answers are really in the browser's storage.
  const kept = outbox.durable()
  return (
    <Page width={560}>
      <Shell exitMode="done" onExit={onHome}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Mascot size={72} mood={offline ? 'sleepy' : 'happy'} label="" />
          <Heading sub={!offline
            ? 'Nearly there.'
            : kept
              ? 'They are safe on this device, and will be sent when the internet comes back. You can go Home now.'
              : 'Keep this page open until the internet comes back. If it is closed, these answers will be lost.'}>
            {offline ? 'Finished! Waiting for the internet' : `Saving your answers (${waiting} left)`}
          </Heading>
        </div>
        <Button tone="ghost" onClick={onHome}>Home</Button>
      </Shell>
    </Page>
  )
}
